import { useEffect, useState } from "react";
import { IconCheck, IconPencil, IconTrash } from "@tabler/icons-react";
import { useCecosParaSolicitud, useCuentasParaSolicitud, useIndicadoresImpuesto } from "@/features/catalogos/hooks/use-catalogos";
import { SolicitarElementoNoListado } from "@/features/solicitudes-catalogo/components/SolicitarElementoNoListado";
import { useEditarSubLinea, useEliminarLinea } from "@/features/prorrateo/hooks/use-prorrateo-mutations";
import { useSociedadDeProrrateo } from "./ProrrateoSociedadContext";
import type { LineaProrrateo } from "@/features/prorrateo/types";
import { Badge, IconAction, ImporteInput, Select, Td, Text, Tr } from "@/components/ui";
import { useFormatoImporte, useMonedaDeProrrateo } from "./ProrrateoSociedadContext";
import { CodigoConNombre } from "./CodigoConNombre";

const GUID_VACIO = "00000000-0000-0000-0000-000000000000";

interface Props {
  linea: LineaProrrateo;
  disabled: boolean;
  solicitudId: string;
  /** true si el concepto tiene más de una sub-línea (se puede eliminar esta). */
  puedeEliminar: boolean;
  /** Slot del CFDI (Múltiples CFDI) · omitir para CFDI único. */
  numeroCfdi?: number;
  /**
   * Edición DIFERIDA: cuando el prorrateo tiene MUCHAS sub-líneas (p. ej. una
   * dispersión de Excel con cientos de CeCos), montar 3 `<Select>` de Radix por
   * fila (cientos en total) satura la memoria del navegador. Con este flag la
   * fila se muestra como texto de solo lectura y solo despliega los selects
   * cuando el usuario pincha "editar" · así hay a lo sumo unos pocos montados.
   */
  edicionDiferida?: boolean;
}

/**
 * Fila de una SUB-LÍNEA de concepto · editable inline (autoguardado). El usuario puede
 * cambiar Centro de costo, Cuenta contable (default del concepto), Importe e Indicador.
 * La clave/descripción/importe del concepto viven en el encabezado del concepto (no aquí).
 * Se puede eliminar mientras el concepto conserve al menos una sub-línea.
 */
export function SubLineaRow({ linea, disabled, solicitudId, puedeEliminar, numeroCfdi, edicionDiferida = false }: Props) {
  const editar = useEditarSubLinea(solicitudId, numeroCfdi);
  const eliminar = useEliminarLinea(solicitudId, numeroCfdi);

  const moneda = useMonedaDeProrrateo();
  const fmt = useFormatoImporte();
  const [importe, setImporte] = useState(linea.importe);
  useEffect(() => setImporte(linea.importe), [linea.importe]);

  // En edición diferida la fila arranca colapsada (solo texto) · se expande a
  // demanda. Sin edición diferida siempre está en modo editable (comportamiento
  // previo · pocos renglones).
  const [editando, setEditando] = useState(false);
  const modoTexto = disabled || (edicionDiferida && !editando);

  const sinCuenta = !linea.cuentaContableCodigo;
  const sociedad = useSociedadDeProrrateo();

  return (
    <Tr>
      <Td className="pl-8 font-mono text-muted-foreground">{linea.numeroLinea}</Td>
      {/* CeCo */}
      <Td>
        {modoTexto ? (
          <CodigoConNombre codigo={linea.centroDeCostoCodigo} nombre={linea.centroDeCostoNombre} />
        ) : (
          <div className="flex items-center gap-1">
            <CeCoSelect linea={linea} onChange={(centroDeCostoId) => guardar(editar, linea, { centroDeCostoId })} />
            <SolicitarElementoNoListado tipo="CentroDeCosto" numeroSociedad={sociedad} solicitudPagoId={solicitudId} />
          </div>
        )}
      </Td>
      {/* Cuenta · editable (default del concepto) */}
      <Td>
        {modoTexto ? (
          <CodigoConNombre codigo={linea.cuentaContableCodigo} nombre={linea.cuentaContableNombre} vacio="Sin cuenta" peligro />
        ) : (
          <div className="flex items-center gap-1">
            <CuentaSelect linea={linea} sinCuenta={sinCuenta} onChange={(cuentaContableId) => guardar(editar, linea, { cuentaContableId })} />
            <SolicitarElementoNoListado tipo="CuentaContable" numeroSociedad={sociedad} solicitudPagoId={solicitudId} />
          </div>
        )}
      </Td>
      {/* Importe */}
      <Td align="right">
        {modoTexto ? (
          <Text size="sm" fw={500}>
            {fmt(linea.importe)}
          </Text>
        ) : (
          <ImporteInput
            size="xs"
            value={importe}
            moneda={moneda}
            onChange={(v) => setImporte(typeof v === "number" ? v : 0)}
            onBlur={() => {
              if (importe !== linea.importe) guardar(editar, linea, { importe });
            }}
            className="w-36"
            wrapperClassName="ml-auto"
          />
        )}
      </Td>
      {/* Indicador */}
      <Td>
        {/* El indicador NO es editable (decisión del usuario): lo determina la tasa de IVA del
            concepto o el contrato anualizado / la orden de compra, y la sub-línea lo hereda. */}
        <IndicadorFijo codigo={linea.indicador} />
      </Td>
      {/* Acciones · editar (diferida) + eliminar */}
      <Td align="right">
        <div className="flex items-center justify-end gap-1">
          {!disabled && edicionDiferida && (
            <IconAction
              label={editando ? "Listo" : "Editar sub-línea"}
              size="sm"
              variant="subtle"
              onClick={() => setEditando((e) => !e)}
            >
              {editando ? <IconCheck size={15} stroke={1.8} /> : <IconPencil size={15} stroke={1.7} />}
            </IconAction>
          )}
          {!disabled && puedeEliminar && (
            <IconAction
              label="Eliminar sub-línea"
              size="sm"
              variant="subtle"
              className="text-peligro-600 hover:bg-peligro-50 dark:hover:bg-peligro-900/40"
              loading={eliminar.isPending}
              onClick={() => eliminar.mutate(linea.id)}
            >
              <IconTrash size={15} stroke={1.7} />
            </IconAction>
          )}
        </div>
      </Td>
    </Tr>
  );
}

type Patch = Partial<{ centroDeCostoId: string; cuentaContableId: string; importe: number; indicador: string }>;

/** Aplica un cambio parcial autoguardando el resto de los campos de la sub-línea. */
function guardar(editar: ReturnType<typeof useEditarSubLinea>, linea: LineaProrrateo, patch: Patch) {
  editar.mutate({
    lineaId: linea.id,
    update: {
      centroDeCostoId: patch.centroDeCostoId ?? linea.centroDeCostoId,
      cuentaContableId: patch.cuentaContableId ?? linea.cuentaContableId,
      importe: patch.importe ?? linea.importe,
      indicador: patch.indicador ?? linea.indicador,
    },
  });
}

/**
 * Los selects (CeCo/Cuenta/Indicador) se aíslan en subcomponentes que SOLO se
 * montan cuando la fila está en modo edición · así los catálogos (que pueden ser
 * grandes) se mapean únicamente para las filas abiertas, no para las cientos que
 * quedan como texto.
 */
function CeCoSelect({ linea, onChange }: { linea: LineaProrrateo; onChange: (v: string) => void }) {
  const { data: cecos } = useCecosParaSolicitud(useSociedadDeProrrateo());
  return (
    <Select
      size="xs"
      options={cecos.map((c) => ({ value: c.id, label: `${c.codigo} · ${c.nombre}` }))}
      value={linea.centroDeCostoId === GUID_VACIO ? undefined : linea.centroDeCostoId}
      onValueChange={onChange}
      placeholder="Buscar CeCo…"
      searchable
      searchPlaceholder="Código o nombre…"
      className="w-56"
    />
  );
}

function CuentaSelect({ linea, sinCuenta, onChange }: { linea: LineaProrrateo; sinCuenta: boolean; onChange: (v: string) => void }) {
  const { data: cuentas } = useCuentasParaSolicitud(useSociedadDeProrrateo());
  if (sinCuenta && cuentas.length === 0) {
    return (
      <Text size="xs" fw={600} c="peligro">
        Sin cuenta
      </Text>
    );
  }
  return (
    <Select
      size="xs"
      options={cuentas.map((c) => ({ value: c.id, label: `${c.codigo} · ${c.nombre}` }))}
      value={linea.cuentaContableId === GUID_VACIO ? undefined : linea.cuentaContableId}
      onValueChange={onChange}
      placeholder="Cuenta…"
      searchable
      searchPlaceholder="Código o nombre…"
      className="w-56"
    />
  );
}

function IndicadorFijo({ codigo }: { codigo: string }) {
  const { data: indicadores } = useIndicadoresImpuesto();
  const info = indicadores.find((i) => i.codigo === codigo);
  const etiqueta = info ? `${codigo} · ${(info.tasaOCuota * 100).toFixed(0)}%` : codigo;
  return (
    <span title="Indicador de impuestos del concepto · lo determina la tasa de IVA o el contrato / la orden de compra; no se modifica.">
      <Badge variant="light" color="slate" uppercase={false}>
        {etiqueta}
      </Badge>
    </span>
  );
}
